import {
  FontWeight,
  LiliFilePicker,
  LiliFilePickerError,
  LiliFilePickerFileType,
  Text,
} from '@lili/lili-frontend-common';

import React, { useCallback, useState } from 'react';
import { Box } from './ui/Box';
import { useVerificationCenterHook } from './logic/useVerificationCenterHook';
import { VerFooter } from './ui/VerFooter';
import { Header } from './ui/Header';
import { InfoSIdeBar } from './ui/InfoSideBar';
import { useFileManagement } from './logic/useFileMangement';
import { businessFileTypesImages } from '@Store/slices/kyb/kyb.types';
import { t } from 'i18next';
import { useVerificationCenterAnalytics } from './VerificationCenterAnalytics';

import { useDebounceHandler } from '@Hooks/utils/useDebounceHandler.tsx';
const maxFileSizeInMb = 15;
const VerificationCenter: React.FC = () => {
  const { activeStep, steps, documents, setActiveStep } = useVerificationCenterHook();
  const { reportActOnVerificationCenter, reportErrorDuringFileUpload } =
    useVerificationCenterAnalytics();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [sideBarVisibile, setSideBarVisible] = useState(false);
  const handleOpenSideBar = () => {
    setSideBarVisible(!sideBarVisibile);
  };
  const { debounceAction } = useDebounceHandler();
  const { setFileValue, mutate, handleFileClear, handleFileUpload, fileValue, isLoading } =
    useFileManagement({
      documents,
      setActiveStep,
      reportActOnVerificationCenter,
      reportErrorDuringFileUpload,
    });

  const isLastStep = activeStep === steps.numberOfSteps - 1;
  const handleContinue = useCallback(() => {
    if (activeStep < steps.numberOfSteps - 1) {
      setFileValue(null);
      setActiveStep(activeStep + 1);
    } else {
      mutate();
    }
  }, [activeStep, steps.numberOfSteps, setFileValue, setActiveStep, mutate]);
  return (
    <div className='w-full h-full items-center flex flex-col pt-[26px]'>
      <div className='flex flex-col max-w-[372px] h-full px-[24px]'>
        <Header
          activeStep={activeStep}
          docTitle={documents.title}
          numberOfSteps={steps.numberOfSteps}
        />

        {documents.texts && (
          <Box contentMap={documents.texts} headline='The document must contain:' />
        )}
        <div className='flex gap-1 mb-[32px] px-[24px]'>
          <Text.H9 className='text-text-main-secondary'>
            {t('pages.verificationCenter.theDocument')}
          </Text.H9>
          <div
            onClick={() => {
              handleOpenSideBar();
            }}>
            <Text.H9
              className='text-text-brand-primary underline cursor-pointer'
              fontWeight={FontWeight.SEMIBOLD}
              id='required-doc__preview-link-and-text'>
              {t('pages.verificationCenter.shouldLookLike')}
            </Text.H9>
          </div>
        </div>
        <div className='mb-[12px] pl-1 '>
          <LiliFilePicker
            onFileClear={handleFileClear}
            maxFileSizeInMb={maxFileSizeInMb}
            onError={(error) => {
              if (error == LiliFilePickerError.FILE_TOO_BIG) {
                const message = t('enErrors:filePicker.maxSize', { maxSize: maxFileSizeInMb });
                setErrorMessage(message);
              } else if (error == LiliFilePickerError.UNSUPPORTED_TYPE) {
                const message = t('enErrors:filePicker.invalidFileType', {
                  type: 'JPEG, PDF, PNG',
                });
                setErrorMessage(message);
              }
              setFileValue(null);
            }}
            onFileChange={(file) => {
              setErrorMessage(undefined);

              handleFileUpload(file);
            }}
            supportedFormatsTitle={t('pages.verificationCenter.supportedFormates')}
            title={t('pages.verificationCenter.addAnAttachment')}
            value={fileValue}
            setValue={setFileValue}
            errorMessage={errorMessage}
            acceptedTypes={[
              LiliFilePickerFileType.JPEG,
              LiliFilePickerFileType.PDF,
              LiliFilePickerFileType.PNG,
            ]}
            clearButtonId='remove-file'
            addButtonId='add-file'
            id={'requiredDocFileUpload'}
          />
        </div>
        <div className='flex w-full self-stretch mb-[32px] p-[8px] justify-center rounded-[4px] align-center gap-[10px] bg-surface-brand-action-00 '>
          <Text.H9 className='text-text-main-secondary'>
            {t('pages.verificationCenter.screenshots')}
          </Text.H9>
        </div>
      </div>
      <VerFooter
        onClick={() => debounceAction(handleContinue)}
        text={isLastStep ? t('global.submit') : t('global.FORM_SUBMIT')}
        isLoading={isLoading}
        disabled={!fileValue}
      />
      <InfoSIdeBar
        sideBarVisibile={sideBarVisibile}
        setSideBarVisible={() => handleOpenSideBar()}
        images={
          businessFileTypesImages[steps.activeStepName as keyof typeof businessFileTypesImages]
        }
      />
    </div>
  );
};

export default VerificationCenter;
